import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface SourceItem { source: string; count: number; percent: number; }
interface DeviceItem { device: string; count: number; percent: number; }
interface RegionItem { region: string; count: number; percent: number; }

const SOURCE_COLORS = ['#0d9488', '#10b981', '#f59e0b', '#6366f1', '#ec4899'];
const DEVICE_COLORS = ['#0d9488', '#10b981', '#f59e0b'];

function classifyReferrer(referrer: string | null): string {
  if (!referrer) return '直接访问';
  if (/baidu\.com/i.test(referrer)) return '百度搜索';
  if (/google\.com/i.test(referrer)) return 'Google';
  if (/bing\.com/i.test(referrer)) return 'Bing';
  if (/weixin|wechat/i.test(referrer)) return '微信';
  return '其他来源';
}

export default function TrafficAnalysis() {
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [topRegion, setTopRegion] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from('site_visits')
          .select('referrer, device_type, region')
          .gte('created_at', thirtyDaysAgo);

        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }

        const total = data.length;

        // 流量来源统计
        const srcMap: Record<string, number> = {};
        data.forEach((r) => {
          const src = classifyReferrer(r.referrer);
          srcMap[src] = (srcMap[src] || 0) + 1;
        });
        const srcList = Object.entries(srcMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([source, count]) => ({
            source,
            count,
            percent: Math.round((count / total) * 100),
          }));
        setSources(srcList);

        // 设备分布统计
        const devMap: Record<string, number> = {};
        data.forEach((r) => {
          const dev = r.device_type || '桌面端';
          devMap[dev] = (devMap[dev] || 0) + 1;
        });
        const devList = Object.entries(devMap)
          .sort((a, b) => b[1] - a[1])
          .map(([device, count]) => ({
            device,
            count,
            percent: Math.round((count / total) * 100),
          }));
        setDevices(devList);

        // 地区分布统计
        const regMap: Record<string, number> = {};
        data.forEach((r) => {
          const reg = r.region || '未知地区';
          regMap[reg] = (regMap[reg] || 0) + 1;
        });
        const regList = Object.entries(regMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 7)
          .map(([region, count]) => ({
            region,
            count,
            percent: Math.round((count / total) * 100),
          }));
        setRegions(regList);
        if (regList.length > 0) setTopRegion(regList[0].region);
      } catch (err) {
        console.error('TrafficAnalysis fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 饼图扇形路径
  const buildPieSlices = (items: { percent: number }[]) => {
    let startAngle = -90;
    return items.map((item, i) => {
      const angle = (item.percent / 100) * 360;
      const endAngle = startAngle + angle;
      const r = 50; const cx = 60; const cy = 60;
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startAngle));
      const y1 = cy + r * Math.sin(toRad(startAngle));
      const x2 = cx + r * Math.cos(toRad(endAngle));
      const y2 = cy + r * Math.sin(toRad(endAngle));
      const largeArc = angle > 180 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      startAngle = endAngle;
      return { d, color: SOURCE_COLORS[i % SOURCE_COLORS.length] };
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((j) => <div key={j} className="h-8 bg-gray-100 rounded"></div>)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const noData = sources.length === 0 && devices.length === 0;

  if (noData) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {['流量来源分布', '设备类型分布', '访客地区分布'].map((title) => (
          <div key={title} className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col items-center justify-center min-h-[200px]">
            <i className="ri-bar-chart-2-line text-3xl text-gray-200 mb-2"></i>
            <p className="text-sm font-medium text-gray-700 mb-1">{title}</p>
            <p className="text-xs text-gray-400">暂无访问数据</p>
          </div>
        ))}
      </div>
    );
  }

  const pieSlices = buildPieSlices(sources);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* 流量来源 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">流量来源分布</h3>
        <p className="text-xs text-gray-400 mb-4">各渠道访问占比（近30天）</p>
        <div className="flex justify-center mb-4">
          <svg viewBox="0 0 120 120" className="w-28 h-28">
            {pieSlices.map((s, i) => (
              <path key={i} d={s.d} fill={s.color} stroke="white" strokeWidth="1.5" />
            ))}
            <circle cx="60" cy="60" r="28" fill="white" />
            <text x="60" y="64" textAnchor="middle" fontSize="11" fill="#374151" fontWeight="bold">
              {sources.reduce((a, b) => a + b.count, 0)}
            </text>
          </svg>
        </div>
        <div className="space-y-2">
          {sources.map((s, i) => (
            <div key={s.source} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[i % SOURCE_COLORS.length] }}></div>
                <span className="text-xs text-gray-600">{s.source}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{s.count.toLocaleString()}</span>
                <span className="text-xs font-medium text-gray-700 w-10 text-right">{s.percent}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 设备分布 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">设备类型分布</h3>
        <p className="text-xs text-gray-400 mb-4">访问设备占比（近30天）</p>
        <div className="space-y-4">
          {devices.map((d, i) => (
            <div key={d.device}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] }}></div>
                  <span className="text-sm text-gray-700">{d.device}</span>
                </div>
                <span className="text-sm font-semibold text-gray-800">{d.percent}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${d.percent}%`, backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] }}
                ></div>
              </div>
              <div className="text-xs text-gray-400 mt-1">{d.count.toLocaleString()} 次访问</div>
            </div>
          ))}
        </div>
        {devices.length > 1 && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-500 mb-2">设备占比可视化</div>
            <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
              {devices.map((d, i) => (
                <div
                  key={d.device}
                  className="h-full transition-all duration-700"
                  style={{ width: `${d.percent}%`, backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] }}
                  title={`${d.device}: ${d.percent}%`}
                ></div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 地区分布 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">访客地区分布</h3>
        <p className="text-xs text-gray-400 mb-4">Top 7 访问地区（近30天）</p>
        {regions.length === 0 || (regions.length === 1 && regions[0].region === '未知地区') ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <i className="ri-map-pin-line text-3xl mb-2"></i>
            <p className="text-xs text-center">地区数据需配置 IP 解析服务后显示</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {regions.map((r, i) => (
                <div key={r.region} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-4 text-right flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-gray-700 w-20 flex-shrink-0 truncate">{r.region}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${r.percent}%`, backgroundColor: i === 0 ? '#0d9488' : i < 3 ? '#10b981' : '#d1fae5' }}
                    ></div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-10 text-right flex-shrink-0">{r.percent}%</span>
                </div>
              ))}
            </div>
            {topRegion && topRegion !== '未知地区' && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">{topRegion}访客占比最高</span>
                <span className="text-sm font-bold text-teal-600">{regions[0]?.percent}%</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
